import { useCustomMetadataModal } from "hooks/useCustomMetadataModal";
import { useModalOperatorsAndValues } from "hooks/CustomMetadata/useModalOperatorsAndValues";
import { CustomMetadata } from "types";
import { CustomNameEnumIcon } from "assets/icons/CustomMetadataIcons";
import QueryModalInput from "views/components/QueryModal/QueryModalInput";
import BaseQueryModal from "./BaseQueryModal";
import { useMultiSelectQueryModal } from "hooks/UseQueryModal/useMultiSelectQueryModal";

interface EnumModalProps {
  field: CustomMetadata;
  editQueryId?: string;
}

export default function EnumModal({ editQueryId, field }: EnumModalProps) {
  const { modalRef, handleSubmission, fieldName } = useCustomMetadataModal({
    field,
    editQueryId,
  });

  const {
    operator,
    setOperator,
    operatorOptions,
    values,
    setValues,
    selectedIds,
    searchText,
    setSearchText,
    previousValue,
    previousOperator,
    existingQuery,
  } = useModalOperatorsAndValues({
    type: "enum",
    field,
    editQueryId,
    options: field.enum_options || [],
  });

  const previousValueIds = previousValue
    ? values.filter((v) => previousValue.includes(v.text)).map((v) => v.id)
    : [];

  const { isSubmitDisabled } = useMultiSelectQueryModal({
    selectedIds,
    previousIds: previousValueIds,
    selectedOperator: operator,
    initialOperator: previousOperator,
    editQueryId,
    existingQueryId: existingQuery?.id,
  });

  const handleSubmit = () => {
    if (isSubmitDisabled) return;
    const selectedValues = values.filter((v) => v.selected).map((v) => v.text);
    handleSubmission(operator, selectedValues);
  };

  const operators = {
    options: operatorOptions,
    selected: operator,
    onSelect: setOperator,
  };

  const handleRemoveValue = (id: number | string) => {
    setValues(values.map((v) => (v.id === id ? { ...v, selected: false } : v)));
  };

  const handleSelectValue = (id: number | string) => {
    setValues(
      values.map((v) => (v.id === id ? { ...v, selected: !v.selected } : v)),
    );
  };

  const handleInputChange = (value: string) => {
    setSearchText(value);
  };

  return (
    <BaseQueryModal<typeof operator>
      title={fieldName}
      keepOpenOnLoading
      modalRef={modalRef}
      icon={<CustomNameEnumIcon />}
      isSubmitDisabled={isSubmitDisabled}
      onSubmit={handleSubmit}
      operators={operators}
    >
      <QueryModalInput
        mode="multiple"
        options={values}
        searchText={searchText}
        selected={selectedIds}
        onRemove={handleRemoveValue}
        onSelect={handleSelectValue}
        onInputChange={handleInputChange}
      />
    </BaseQueryModal>
  );
}
